import { setGA4Event } from "@iewc/Store/Handlers/Utilities/CustomTracking";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentPage, getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import { DocumentModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { IconMemo, IconProps } from "@insite/mobius/Icon";
import File from "@insite/mobius/Icons/File";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

// Verndale Customization
const mapStateToProps = (state: ApplicationState) => {
    const location = getLocation(state);

    return {
        pageTitle: getCurrentPage(state).fields.title,
        location,
    };
};
// End Verndale Customization

type OwnProps = WidgetProps & HasProduct;
type Props = ReturnType<typeof mapStateToProps> & OwnProps;

export interface ProductDetailsDocumentsStyles {
    wrapper?: InjectableCss;
    container?: GridContainerProps;
    documentGridItem?: GridItemProps;
    documentLink?: LinkPresentationProps;
    documentIcon?: IconProps;
    documentNameText?: TypographyPresentationProps;
}

export const documentsStyles: ProductDetailsDocumentsStyles = {
    wrapper: {
        css: css`
            padding: 10px 15px 30px;
        `,
    },
    documentGridItem: {
        width: [12, 12, 6, 6, 6],
    },
    documentIcon: {
        src: File,
        color: "text.link",
        css: css`
            margin-right: 10px;
        `,
    },
    documentNameText: {
        css: css`
            vertical-align: super;
        `,
    },
};

const styles = documentsStyles;

const ProductDetailsDocuments: React.FC<Props> = ({ product: { documents }, location, pageTitle }) => {
    if (!documents || documents.length === 0) {
        return null;
    }

    // Verndale Customization
    const handleClickedDocument = (e: React.MouseEvent<HTMLAnchorElement>, document: DocumentModel) => {
        e.stopPropagation();

        const currentLocation = window.location;

        // Add custom code here to track document clicks
        setGA4Event([], "file_download", {
            file_name: document.name,
            file_extension: document.documentType,
            link_text: document.name,
            link_url: currentLocation.href,
            pageTitle,
        });

        return true;
    };
    // End Verndale Customization

    return (
        <StyledWrapper {...styles.wrapper}>
            <GridContainer {...styles.container}>
                {documents.map(document => (
                    <GridItem key={document.id.toString()} {...styles.documentGridItem}>
                        <Link
                            href={document.filePath}
                            target="_new"
                            {...styles.documentLink}
                            data-test-selector={`productDetails_${document.id}`}
                            onClick={(e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) =>
                                handleClickedDocument(e, document)
                            }
                        >
                            <IconMemo {...styles.documentIcon} />
                            <Typography {...styles.documentNameText}>{translate("Spec Sheet")}</Typography>
                        </Link>
                    </GridItem>
                ))}
            </GridContainer>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(withProduct(ProductDetailsDocuments) as any) as any, // Verndale Customization
    definition: {
        displayName: "Documents",
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
    },
};

export default widgetModule;
